// admin-frontend/src/components/fieldStyles.ts
// ─────────────────────────────────────────────────────────────────
// Phase R7 follow-up (2026-05-13): single source of truth for the
// admin-frontend form-field look. Before this module existed, every
// panel re-declared its own className string for <input>/<select>/
// <textarea> and the result was visually inconsistent — three or
// four different padding/radius/focus-ring combinations across the
// product.
//
// Use:
//   import { INPUT_CLS, INPUT_SM_CLS } from '@/components/fieldStyles';
//   <input className={INPUT_CLS} ... />
//   <select className={INPUT_CLS + ' tabular-nums'} ... />
//
// If you find yourself wanting to compose extra Tailwind modifiers
// onto an input, append them with a space — the className strings
// below are designed to merge cleanly with `tabular-nums`,
// `max-w-xs`, `font-mono`, etc.
//
// Phase 6 UI overhaul (2026-05-16): tokens realigned to the unified
// arena/neon design system locked in during Phases 1–5. Hard borders
// → soft arena-tinted inset rings (`ring-1 ring-inset` so layout
// doesn't shift between the dim resting state and a 2-px focus ring),
// neutral palette → arena/neon, fuchsia focus → admin violet. Every
// consumer (PlatformPanel modal, ReassignPanel, ReassignModal,
// AssignPlayersToAgentModal, etc.) picks up the new look automatically.
//
// Variants
// ────────
//   INPUT_CLS         — default fill-the-row input. The "Tier A"
//                       canonical pattern. Use everywhere unless you
//                       have a deliberate reason not to.
//   INPUT_SM_CLS      — compact inline-edit pattern (e.g. rename
//                       button on a card). Smaller radius + padding.
//   INPUT_INVALID_CLS — append to INPUT_CLS when a required field
//                       has a validation error. Switches the focus
//                       ring + border to rose.
//
// All variants:
//   * Respect dark mode automatically.
//   * Have an explicit focus state (no browser default outline).
//   * Use the platform's violet accent (matches admin brand identity).
//   * Reserve space for placeholder text without layout shift.
// ─────────────────────────────────────────────────────────────────

// 2026-05-20 — Replicate the agent-frontend's input aesthetic.
//
// The previous dark-mode token (`dark:bg-arena-800` solid + `ring-inset`)
// was technically legible but visually subtle compared to the agent
// app. The agent app uses a *lighter* fill (`arena-700/40`) plus a
// hard BORDER (`arena-600/50`) — that combination reads as a much
// crisper, more obviously-interactive form control. Mirror that
// pattern here byte-for-byte so the two SPAs feel like the same
// product. Focus state matches: violet ring + violet border.
//
// Light-mode tokens were already aligned (white fill + arena-tinted
// border) so no change there.
export const INPUT_CLS =
  'w-full rounded-xl px-3 py-2 text-[14px] ' +
  'bg-white dark:bg-arena-700/40 ' +
  'border border-arena-200/60 dark:border-arena-600/50 ' +
  'text-arena-900 dark:text-white ' +
  'placeholder-arena-400 dark:placeholder-neon-200/55 ' +
  'focus:outline-none focus:ring-2 focus:ring-violet-400/40 focus:border-violet-400 ' +
  'disabled:opacity-50 disabled:cursor-not-allowed';

// 2026-05-20 — Mirror of the agent-frontend INPUT_DATE_CLS so date
// inputs don't blow out to full row width on mobile (iOS Safari
// pads them with the picker icon and a w-full date field looks
// oversized on every breakpoint).
export const INPUT_DATE_CLS =
  'rounded-xl px-3 py-2 text-[14px] ' +
  'w-full max-w-[10rem] sm:max-w-[12rem] ' +
  'bg-white dark:bg-arena-700/40 ' +
  'border border-arena-200/60 dark:border-arena-600/50 ' +
  'text-arena-900 dark:text-white ' +
  'placeholder-arena-400 dark:placeholder-neon-200/55 ' +
  'focus:outline-none focus:ring-2 focus:ring-violet-400/40 focus:border-violet-400 ' +
  'disabled:opacity-50 disabled:cursor-not-allowed';

export const INPUT_SM_CLS =
  'rounded-lg px-2 py-1 text-[12.5px] ' +
  'bg-white dark:bg-arena-700/40 ' +
  'border border-arena-200/60 dark:border-arena-600/50 ' +
  'text-arena-900 dark:text-white ' +
  'placeholder-arena-400 dark:placeholder-neon-200/55 ' +
  'focus:outline-none focus:ring-2 focus:ring-violet-400/40 focus:border-violet-400 ' +
  'disabled:opacity-50 disabled:cursor-not-allowed';

// Append to INPUT_CLS when a field has a validation error. Switches
// both the border and focus ring to rose.
export const INPUT_INVALID_CLS =
  ' border-rose-400/70 dark:border-rose-500/60 focus:ring-rose-500/60 focus:border-rose-500';

// Convenience helper for cases where you want to compose conditionally.
export function cx(...parts: Array<string | false | null | undefined>): string {
  return parts.filter(Boolean).join(' ');
}
